import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createStandaloneAction,
  deleteAction,
  deleteSubAction,
  getActions,
  getArchivedActions,
  getResponsibleOptions,
  uploadActionAttachments,
} from '../api/actionApi'
import type {
  ActionDetail,
  ActionQuery,
  ActionScope,
  ResponsibleRole,
  StandaloneActionCreateRequest,
} from '../types'
import type { AppFeedbackTriggerKind } from '../../../lib/appFeedback/trigger'

export interface StandaloneActionCreateArgs {
  body: StandaloneActionCreateRequest
  /** Oluşturma anında seçilen dokümanlar. Boş/verilmemiş olabilir — ek isteğe bağlıdır. */
  files?: File[]
}

export interface StandaloneActionCreateResult {
  action: ActionDetail
  /** Görev oluştu ama ek(ler) yüklenemedi. Çağıran başarı yerine <b>uyarı</b> gösterir. */
  filesFailed: boolean
}

/**
 * Toplantıdan bağımsız aksiyon oluştur (6.5.3) → aksiyon listelerini tazele.
 *
 * <p><b>İki adımlıdır:</b> önce kayıt (`POST /management/actions`), sonra varsa ekler
 * (`POST /{id}/attachments`) — tek multipart uç yoktur ve eklenmesine gerek de yoktu. Aynı kalıbın
 * projedeki emsalleri: {@code useCreateActivity}, {@code MinutesCreatePage.submitCreate},
 * {@code useUpdateMeeting}. İkinci adım kaydın id'sine muhtaç olduğu için sıra zorunludur.
 *
 * <p><b>Dosya adımının hatası YUTULUR</b> ve {@code filesFailed} ile bildirilir — emsal
 * {@code useCreateActivity} burada reddediyor, bu hook bilinçli olarak ayrışıyor. Sebep ölçülebilir:
 * reddedilirse mutation başarısız sayılır, dialog açık kalır ve merkezî "bir hata oluştu" toast'ı
 * çıkar; oysa <b>görev gerçekten oluşmuştur</b>. Kullanıcının oradaki tek makul davranışı "Kaydet"e
 * yeniden basmaktır ve o da <b>ikinci bir görev</b> yaratır. Yutarak: liste tazelenir, dialog
 * kapanır, kullanıcı "görev oluştu ama dosyalar eklenemedi" uyarısını görür ve dosyayı Görev
 * Detayı → Dokümanlar'dan ekleyebilir.
 *
 * <p><b>Yetki notu:</b> oluşturma {@code ACTION_CREATE}, ek yükleme {@code ACTION_MANAGE} ister
 * (bilinçli olarak ayrı kodlar — bkz. {@code Grants}). 28.09.2026'da canlı şemada ölçüldü:
 * {@code ACTION_CREATE} taşıyıp {@code ACTION_MANAGE} taşımayan <b>rol yok</b>, dolayısıyla akış
 * pratikte tek yetkiyle çalışır. Yapısal bir garanti değildir: rol yetkileri yönetim ekranından
 * değiştirilebilir ve o gün ek adımı 403 alır — {@code filesFailed} yolu tam da bunu sessiz
 * bırakmaz.
 */
export function useCreateStandaloneAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      body,
      files = [],
    }: StandaloneActionCreateArgs): Promise<StandaloneActionCreateResult> => {
      const action = await createStandaloneAction(body)
      if (files.length === 0) return { action, filesFailed: false }
      try {
        await uploadActionAttachments(action.id, files)
        return { action, filesFailed: false }
      } catch {
        return { action, filesFailed: true }
      }
    },
    // Vekil modunda aksiyon ekleme yasaktır (089): buton zaten görünmez (yetki düşer), ama doğrudan
    // denenirse 403 gelir — genel "yetkiniz yok" yerine nedeni söyleyen metin gösterilir.
    // appFeedback (096): Görev oluşturma, THY deneyim anketinin tetikleyicilerinden biridir.
    // Tetikleme main.tsx'teki MutationCache.onSuccess'ten yapılır; burada yalnız işaretlenir.
    meta: {
      proxyBlockedKey: 'proxy.actionCreateBlocked',
      appFeedback: 'create' satisfies AppFeedbackTriggerKind,
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['actions'] })
      qc.invalidateQueries({ queryKey: ['archived-actions'] })
      // Yeni aksiyonun sorumluları süzgeç seçeneklerine girer.
      qc.invalidateQueries({ queryKey: ['action-responsibles'] })
    },
  })
}

/**
 * **Liste satırından alt görev sil** (2026-09-17) → aksiyon + arşiv listelerini ve ÜST görevin
 * detayını tazele.
 *
 * <p><b>Neden ayrı uç:</b> kök görev ucu ({@code DELETE /actions/{id}}) alt görevin yan etkilerini
 * yapmaz. Alt görev ucu, **otomatik** alt görevde onu doğuran Süreç Destek Yöneticisi atamasını
 * üstten de kaldırır (yoksa sorumlu senkronu kaydı yeniden üretir → "geri gelen görev"), denetim
 * izine {@code SUBACTION_DELETED} yazar ve güncel üst detayı döndürür.
 *
 * <p><b>Neden {@code useDeleteSubAction} (detay) kullanılmıyor:</b> o hook detay sayfası içindir ve
 * dönen üst detayı **o sayfanın** anahtarına yazar; listede böyle bir bağlam yok, tazelenmesi gereken
 * şey listelerdir. İkisi aynı ucu çağırır, önbellek işi farklıdır.
 *
 * <p>{@code handledCodes}: 400 (VALIDATION_ERROR) merkezî "bir hata oluştu" toast'ına düşmez — çağıran
 * nedeni söyleyen metni gösterir (bkz. {@code subActionDeleteErrorKey}); **{@code onError} zorunludur.**
 */
export function useDeleteListSubAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteSubAction(id),
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSuccess: (parent) => {
      qc.invalidateQueries({ queryKey: ['actions'] })
      qc.invalidateQueries({ queryKey: ['archived-actions'] })
      qc.invalidateQueries({ queryKey: ['action-responsibles'] })
      // Üst görevin detayı bayatlar: alt görev kartı ve (otomatik olanda) Süreç Destek Yöneticisi
      // satırı oradan okunur. Anahtar öneki `useActionDetail`in KEY'i ile aynıdır (dil son bileşendir).
      qc.invalidateQueries({ queryKey: ['action-detail', parent.id] })
    },
  })
}

/** Aksiyonu sil (067/6.5.5) → aksiyon + arşiv listelerini tazele. */
export function useDeleteAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteAction(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['actions'] })
      qc.invalidateQueries({ queryKey: ['archived-actions'] })
      // Silinen aksiyonun sorumluları başka aksiyonda yoksa süzgeç seçeneklerinden düşer.
      qc.invalidateQueries({ queryKey: ['action-responsibles'] })
    },
  })
}

/**
 * Aksiyon listesi sunucu-durumu (044/6.4). {@code archived=true} → Arşivlenen Aksiyonlar ucu (Arşivlendi/İptal),
 * aksi halde aktif Tüm Aksiyonlar. Sayfa/sıra/filtre değişiminde önceki veriyi koru (titremesiz). Aktif dil
 * ({@code i18n.language}) key'e girer: durum adı sunucuda dile göre çözülür → dil değişince tablo yenilenir.
 */
export function useActions(query: ActionQuery, archived = false) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: [
      archived ? 'archived-actions' : 'actions',
      i18n.language,
      query.scope ?? 'all',
      query.q ?? '',
      query.importanceLevelId ?? null,
      (query.statusIds ?? []).join(','),
      query.criticalOrOverdue ?? false,
      query.overdueOnly ?? false,
      query.upcomingOnly ?? false,
      query.delegatedOnly ?? false,
      query.period ?? '',
      query.periodFrom ?? '',
      query.periodTo ?? '',
      query.subAction ?? null,
      query.delayedUpdated ?? false,
      query.mainResponsibleEmployeeId ?? '',
      query.supportManagerEmployeeId ?? '',
      query.page ?? 0,
      query.size ?? 20,
      query.sort ?? '',
    ],
    queryFn: () => (archived ? getArchivedActions(query) : getActions(query)),
    placeholderData: keepPreviousData,
  })
}

/**
 * Kişi süzgeçlerinin seçenekleri — kapsamda **istenen rolde** atanmış kişiler.
 *
 * <p>Ekran süzgeçlerinden bağımsızdır (yalnız kapsam + rol key'e girer) → filtre değiştikçe yeniden
 * istek atılmaz. Yalnız aktif listelerde anlamlıdır; arşiv ekranında `enabled=false` ile kapatılır.
 *
 * <p>**Rol neden key'de:** süzgeçler 2026-09-15'te ayrıldı ve her kutu yalnız kendi rolündeki kişileri
 * listeler. Tek bir birleşik liste bırakılsaydı kullanıcı "Ana Sorumlu" kutusundan yalnızca Süreç
 * Destek Yöneticisi olan birini seçebilir ve sebebi ekranda görünmeyen boş bir sonuç alırdı.
 */
export function useResponsibleOptions(
  scope: ActionScope | undefined,
  enabled = true,
  role?: ResponsibleRole,
) {
  return useQuery({
    queryKey: ['action-responsibles', scope ?? 'all', role ?? 'all'],
    queryFn: () => getResponsibleOptions(scope, role),
    enabled,
  })
}
